import {IRouteStore} from '../../router/routing.infra';
import {getRootStore} from '../../app.bootstrap';
import {RouteName} from '../../router/route-names';
import {VjHistoryController, IVjHistoryController, VjSession} from './controller';
import {getDashboardLanguage} from '../helper/general.helper';

type SessionData = {
    interactionSummaryVjPreviews?: VjSession[];
    startDateValue?: Date | string;
    endDateValue?: Date | string;
    customerRef?: string;
};

export interface VjHistoryUrlParams {
    customerRef?: string;
    startDate?: string;
    endDate?: string;
    roomId?: string;
    isEmbedded: boolean;
}

export interface IVjHistoryStore extends IRouteStore {
    vjHistoryController: IVjHistoryController | undefined;
    localizationService: any;
    dispose(): void;
}

export class VjHistoryStore implements IVjHistoryStore {
    public vjHistoryController: IVjHistoryController | undefined;
    public localizationService: any;

    private readonly _browserUtilsService: any;
    private readonly _urlParams: {endDate?: string; customerRef?: string; startDate?: string};
    private readonly _isEmbedded: boolean;
    private readonly _roomId: string;

    constructor(accountData: any, urlParams: VjHistoryUrlParams) {
        const rootStore = getRootStore();
        this.localizationService = rootStore.localizationService;
        this._browserUtilsService = rootStore.browserUtilsService;

        this._urlParams = {
            customerRef: urlParams.customerRef,
            startDate: urlParams.startDate,
            endDate: urlParams.endDate
        };
        this._isEmbedded = urlParams.isEmbedded;
        this._roomId = urlParams.roomId ?? '';

        const isInitialized = this.localizationService.isInitialized();
        if (!isInitialized && accountData?._id) {
            const accountLanguage = getDashboardLanguage(accountData);
            this.localizationService.setAccountData(accountData._id, accountLanguage);
        }

        const searchCriteria = this._getSearchCriteria();
        const goToInvitePage = this._isEmbedded
            ? undefined
            : () => {
                  void rootStore.tsReactStateHelper.navigateTo(RouteName.INVITE);
              };

        this.vjHistoryController = new VjHistoryController(
            this.localizationService.translate.bind(this.localizationService),
            accountData._id,
            accountData.protectedSettings?.reportsDateFormat ?? '',
            accountData.settings?.timezone ?? '',
            accountData.settings?.language ?? '',
            searchCriteria,
            accountData.protectedSettings?.interactionSummary?.historySearchPerPage ?? 25,
            goToInvitePage
        );

        if (this._canUseSessionStorage()) {
            this.vjHistoryController.updateVjList(this._getStoredResults());
        } else {
            this.vjHistoryController.searchHistory();
        }
    }

    private _getSessionData(): SessionData | undefined {
        return this._browserUtilsService.getFromSessionStorage('interactionSummaryVJ') as unknown as SessionData;
    }

    private _canUseSessionStorage(): boolean {
        const urlParamsEmpty =
            Object.values(this._urlParams).every((val) => val === '' || val === undefined) && !this._roomId;
        const sessionDataExists = this._getSessionData() !== undefined;
        return urlParamsEmpty && sessionDataExists;
    }

    private _getSearchCriteria(): typeof this._urlParams & {roomId?: string} {
        if (this._roomId) {
            return {roomId: this._roomId};
        }
        if (!this._canUseSessionStorage()) {
            return this._urlParams;
        }
        const sessionData = this._getSessionData();
        if (sessionData) {
            return {
                customerRef: sessionData.customerRef,
                startDate:
                    sessionData.startDateValue instanceof Date
                        ? sessionData.startDateValue.toString()
                        : sessionData.startDateValue,
                endDate:
                    sessionData.endDateValue instanceof Date
                        ? sessionData.endDateValue.toString()
                        : sessionData.endDateValue
            };
        }
        return {};
    }

    private _getStoredResults(): VjSession[] {
        const sessionData = this._getSessionData();
        return sessionData?.interactionSummaryVjPreviews ?? [];
    }

    dispose(): void {}
}
